import React, { useState } from "react";
import { Modal } from "../../shared/Modal";
import { Input } from "../../shared/Input";
import { FormActions } from "../../shared/FormActions";
import { Icons } from "../../shared/Icons";
import { professionalsApi } from "../../../services/professionalsApi";
import { useToaster } from "../../shared/Toaster";
import type { Professional } from "../../../services/professionalsApi";

interface ResetProfessionalPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  professional: Professional | null;
}

export const ResetProfessionalPasswordModal: React.FC<
  ResetProfessionalPasswordModalProps
> = ({ isOpen, onClose, professional }) => {
  const { showToast } = useToaster();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<{
    newPassword?: string;
    confirmPassword?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = "Nova senha é obrigatória";
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = "A senha deve ter pelo menos 6 caracteres";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirmação de senha é obrigatória";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "As senhas não coincidem";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!professional) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await professionalsApi.resetProfessionalPassword(
        professional.id,
        formData.newPassword
      );

      if (response.success) {
        showToast(
          "success",
          `Senha do profissional ${professional.name} alterada com sucesso!`
        );
        setFormData({
          newPassword: "",
          confirmPassword: "",
        });
        setErrors({});
        onClose();
      } else {
        showToast("error", response.error || "Falha ao alterar senha");
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      showToast("error", "Erro ao alterar senha. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!professional) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Alterar Senha - ${professional.name}`}
      icon={<Icons.Lock sx={{ fontSize: 24 }} />}
      size="md"
      headerGradient="blue"
    >
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-sm text-blue-800">
            Você está alterando a senha do profissional{" "}
            <strong>{professional.name}</strong>. A nova senha será definida
            sem necessidade de informar a senha atual.
          </p>
        </div>

        <div className="space-y-2">
          <Input
            type="password"
            label="Nova Senha"
            value={formData.newPassword}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
            placeholder="Digite a nova senha"
            required
            error={errors.newPassword}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Input
            type="password"
            label="Confirmar Nova Senha"
            value={formData.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            placeholder="Confirme a nova senha"
            required
            error={errors.confirmPassword}
            disabled={loading}
          />
        </div>

        <FormActions
          onCancel={handleClose}
          onSubmit={handleSubmit}
          loading={loading}
          submitText="Alterar Senha"
          cancelText="Cancelar"
          submitIcon={<Icons.Lock sx={{ fontSize: 18 }} />}
          submitVariant="primary"
        />
      </div>
    </Modal>
  );
};
